import { remove } from "../../utils/CoreExtensions/ArrayExtensions";
import { createSlice } from "../helper";
import { getComponentByPageAndId, getComponentForTestimonial, getKeyForTestimonialValidation, getPageById, getTestimonialByIdOrTempId, removeValidationErrorForTestimonial } from "./helpers/layoutFlowFormHelper";

// Backend returns: { formData, utilityData }
// formData contains:
// {
//   layoutFlow: {
//     id: integer,
//     title: string,
//     layoutFlowType: integer,
//     status: integer,
//     roleId: integer,
//     abstractProgramId: integer,
//     pages: [
//       {
//         id: integer,
//         position: integer,
//         visible: boolean,
//         layoutFlowId: integer,
//         components: [
//           {
//             id: integer,
//             content: string,
//             refObjectId: integer,
//             refObjectType: string,
//             layoutPageId: integer
//           }
//         ]
//       }
//     ]
//   },
//   testimonials: [],
//   layoutImages: [
//     {
//       id: integer,
//       url: string,
//       imageContentType: string,
//       imageFileSize: integer,
//       imageType: string,
//       abstractProgramId: integer
//     }
//   ]
// }
// utilityData contains API endpoints and config (not stored in Redux)
//

const layoutFlowSlice = createSlice({
  name: "layoutFlowForm",
  initialState: {
    layoutFlow: {},
    testimonials: [],
    layoutImages: [],
    selectedImages: [],
    validationErrors: {}
  },
  reducers: {
    setLayoutFlowForm(state, action) {
      // Expecting action.payload to be formData from backend response
      const { layoutFlow, testimonials, layoutImages } = action.payload;
      state.layoutFlow = layoutFlow;
      state.testimonials = testimonials;
      state.layoutImages = layoutImages;
    },
    setLayoutFlowField(state, action) {
      const { field, value } = action.payload;
      state.layoutFlow[field] = value;
    },
    setLayoutPageVisibility(state, action) {
      const { pageId, visible } = action.payload;
      const pageToUpdate = getPageById(state, pageId);
      if (pageToUpdate) {
        pageToUpdate.visible = visible;
      }
    },
    updateComponentContent(state, action) {
      const { pageId, componentId, content } = action.payload;
      const componentToUpdate = getComponentByPageAndId(state, pageId, componentId);
      if (componentToUpdate) {
        componentToUpdate.content = content;
      }
    },
    updateComponentStyleData(state, action) {
      const { pageId, componentId, styleData } = action.payload;
      const componentToUpdate = getComponentByPageAndId(state, pageId, componentId);
      if (componentToUpdate) {
        componentToUpdate.styleData = { ...componentToUpdate.styleData, ...styleData };
      }
    },
    updateComponentRefObject(state, action) {
      const { pageId, componentId, refObjectId, refObjectType } = action.payload;
      const componentToUpdate = getComponentByPageAndId(state, pageId, componentId);
      if (componentToUpdate) {
        componentToUpdate.refObjectId = refObjectId;
        componentToUpdate.refObjectType = refObjectType;
      }
    },
    updateComponentValidationErrors(state, action) {
      const { componentId, validationResult } = action.payload;
      if (componentId) {
        state.validationErrors[componentId] = validationResult;
      }
    },
    updateTestimonialValidationErrors(state, action) {
      const { testimonialId, fieldKey, validationResult } = action.payload;
      if (testimonialId) {
        const testimonialKey = getKeyForTestimonialValidation(testimonialId, fieldKey)
        if (testimonialKey) {
          state.validationErrors[testimonialKey] = validationResult;
        }
      }
    },
    removeValidationError(state, action) {
      const { componentId, testimonialId, fieldKey } = action.payload;
      if (componentId) {
        delete state.validationErrors[componentId];
      }
      if (testimonialId) {
        removeValidationErrorForTestimonial(state, testimonialId, fieldKey);
      }
    },
    addImageToLayoutImages(state, action) {
      const { imageData } = action.payload;
      state.layoutImages.push(imageData);
    },
    addTestimonial(state, action) {
      const { testimonial } = action.payload;
      state.testimonials.push(testimonial);
    },
    addComponent(state, action) {
      const { pageId, component } = action.payload;
      const pageToUpdate = getPageById(state, pageId);
      if (pageToUpdate) {
        pageToUpdate.components.push(component);
      }
    },
    updateTestimonialField(state, action) {
      const { testimonialId, field, value } = action.payload;
      const testimonial = getTestimonialByIdOrTempId(state, testimonialId);
      if (testimonial) {
        testimonial[field] = value;
      }
    },
    deleteTestimonial(state, action) {
      const { pageId, testimonialId } = action.payload;
      const pageToUpdate = getPageById(state, pageId);
      state.testimonials = state.testimonials.filter(testimonial =>
        testimonial.id !== testimonialId && testimonial.tempId !== testimonialId
      );
      const componentToDelete = getComponentForTestimonial(state, pageId, testimonialId);
      if (pageToUpdate && componentToDelete) {
        pageToUpdate.components = pageToUpdate.components.filter(component =>
          component !== componentToDelete
        );
      }
    },
    addTestimonialImage(state, action) {
      const { testimonialId, imageData } = action.payload;
      const testimonialToUpdate = getTestimonialByIdOrTempId(state, testimonialId);
      if (testimonialToUpdate) {
        testimonialToUpdate.fileName = imageData.file_name;
        testimonialToUpdate.code = imageData.code;
        testimonialToUpdate.url = imageData.tempS3Link;
        testimonialToUpdate.externalUserImageUrl = imageData.tempS3Link;
      }
    },
    removeTestimonialImage(state, action) {
      const { testimonialId } = action.payload;
      const testimonialToUpdate = getTestimonialByIdOrTempId(state, testimonialId);
      if (testimonialToUpdate) {
        testimonialToUpdate.fileName = null;
        testimonialToUpdate.code = null;
        testimonialToUpdate.url = null;
        testimonialToUpdate.externalUserImageUrl = null;
      }
    }
  }
});

export const layoutFlowActions = layoutFlowSlice.actions;

export default layoutFlowSlice;
